import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { usePurchaseSessionVerification } from '../hooks/usePurchaseSessionVerification';
import { getUserProfile } from '../services/user.service';
import { getPlanByCode } from '../config/plans';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/atoms/Button';

const AUTO_REDIRECT_MS = 3000;

export const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation('auth');
  const [isReturningUser, setIsReturningUser] = useState(false);

  const sessionId = searchParams.get('token') || searchParams.get('session_id');
  const providerStatus = searchParams.get('status');

  const { state, isLoading, error, retry, packCode } = usePurchaseSessionVerification(sessionId);
  const planName = packCode ? getPlanByCode(packCode)?.planName : null;

  // Check if user already had onboarding completed (upgrade/renew flow)
  useEffect(() => {
    const check = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await getUserProfile(token);
        if (res.data?.onboarding_completed) setIsReturningUser(true);
      } catch { /* ignore */ }
    };
    check();
  }, [getAccessTokenSilently]);

  const effectiveState =
    state === 'unknown' && providerStatus?.toLowerCase() === 'successful'
      ? 'success'
      : state;

  // Auto-redirect to dashboard once verification succeeds
  useEffect(() => {
    if (effectiveState === 'success') {
      const timer = setTimeout(() => navigate('/dashboard'), AUTO_REDIRECT_MS);
      return () => clearTimeout(timer);
    }
  }, [effectiveState, navigate]);

  // Loading / verifying
  if (isLoading) {
    return (
      <Layout className="bg-white" hideFooter={true}>
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent border-purple mb-3"></div>
          <p className="text-dark-invirtus font-body">{t('payment.finalizing')}</p>
        </div>
      </Layout>
    );
  }

  // Success — welcome message, then auto-redirect
  if (effectiveState === 'success') {
    return (
      <Layout className="bg-white" hideFooter={true}>
        <div className="flex flex-col items-center justify-center py-16 px-4 max-w-md mx-auto">
          <svg className="w-12 h-12 text-green-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <h1 className="text-xl font-semibold text-dark-invirtus mb-2 font-heading">
            {planName ? t('payment.plan_activated', { planName }) : t('payment.plan_activated_generic')}
          </h1>
          <p className="text-dark-invirtus mb-4 text-center font-body">
            {isReturningUser ? t('payment.returning_user_desc') : t('payment.new_user_desc')}
          </p>
          <Button
            label={t('payment.go_to_dashboard')}
            onClick={() => navigate('/dashboard')}
            intent="primary"
            type="contained"
          />
        </div>
      </Layout>
    );
  }

  // Failure or error — send back to pack selection
  if (effectiveState === 'failure' || error) {
    return (
      <Layout className="bg-white" hideFooter={true}>
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <svg className="w-12 h-12 text-red mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <h1 className="text-xl font-semibold text-dark-invirtus mb-2 font-heading">{t('payment.error_title')}</h1>
          <p className="text-dark-invirtus mb-4 text-center font-body">
            {error || t('payment.error_desc')}
          </p>
          <div className="flex gap-3">
            <Button
              label={t('payment.retry')}
              onClick={retry}
              intent="primary"
              type="contained"
            />
            <Button
              label={t('payment.back_to_plans')}
              onClick={() => navigate('/onboarding/pack-selection?payment_error=1')}
              intent="secondary"
              type="outlined"
            />
          </div>
        </div>
      </Layout>
    );
  }

  // Unknown / pending — still polling
  return (
    <Layout className="bg-white" hideFooter={true}>
      <div className="flex flex-col items-center justify-center py-16 px-4 max-w-md mx-auto">
        <svg className="w-12 h-12 text-yellow-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h1 className="text-xl font-semibold text-dark-invirtus mb-2 font-heading">{t('payment.pending_title')}</h1>
        <p className="text-dark-invirtus mb-4 text-center font-body">
          {t('payment.pending_desc')}
        </p>
        <div className="flex gap-3">
          <Button
            label={t('payment.check_again')}
            onClick={retry}
            intent="primary"
            type="contained"
          />
          <Button
            label={t('payment.go_to_dashboard')}
            onClick={() => navigate('/dashboard')}
            intent="secondary"
            type="outlined"
          />
        </div>
      </div>
    </Layout>
  );
};

/**
 * Payment Failure Page
 *
 * Shown when payment is cancelled or fails at the provider level.
 * Redirects back to pack selection so the user can retry.
 */
export const PaymentFailurePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  return (
    <Layout className="bg-white" hideFooter={true}>
      <div className="flex flex-col items-center justify-center py-16 px-4 max-w-md mx-auto">
        <svg className="w-12 h-12 text-red mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
        <h1 className="text-xl font-semibold text-dark-invirtus mb-2 font-heading">{t('payment.failure_title')}</h1>
        <p className="text-dark-invirtus mb-4 text-center font-body">
          {t('payment.failure_desc')}
        </p>
        <Button
          label={t('payment.back_to_plans')}
          onClick={() => navigate('/onboarding/pack-selection?payment_error=1')}
          intent="primary"
          type="contained"
        />
      </div>
    </Layout>
  );
};
